// pages/CheckoutPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronRight,
  Percent,
  Gift,
  ArrowLeft,
  X,
  Shield,
  Truck,
  Clock,
  Loader2,
  CheckCircle,
} from "lucide-react";

import CouponModal from "./Couponmodal";
import { redeemCoupon } from "../../api/coupon";
import ReferralModal from "./Referralmodal";
import { loadRazorpayScript, convertToRupees } from "../../api/razorpay";
import { processPayment } from "./razorpay";

// ─── Main Component ───────────────────────────────────────────────────────────
const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [razorpayOrderId, setRazorpayOrderId] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponRedeemed, setCouponRedeemed] = useState(false);

  // Referral state
  const [referralCode, setReferralCode] = useState("");
  const [referralApplied, setReferralApplied] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralDiscount, setReferralDiscount] = useState(0);
  const [referrerId, setReferrerId] = useState(null);

  // ── Responsive check ─────────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ── Pre-load Razorpay SDK on mount ───────────────────────────────────────────
  useEffect(() => {
    loadRazorpayScript();
  }, []);

  // ── Pull data from navigation state ──────────────────────────────────────────
  const { selectedWorkout, selectedDiet, total } = location.state || {};

  const workoutId = selectedWorkout?._id || selectedWorkout?.id || null;
  const dietId    = selectedDiet?._id   || selectedDiet?.id   || null;
  const isDietOnlyCheckout = !workoutId && !!dietId;

  const workoutPrice = convertToRupees(parseInt(selectedWorkout?.discountedPrice || "0", 10));
  const dietPrice    = convertToRupees(parseInt(selectedDiet?.discountedPrice    || "0", 10));

  const subtotal      = total || workoutPrice + dietPrice;
  const totalDiscount = couponDiscount + referralDiscount;
  const finalTotal    = Math.max(0, subtotal - totalDiscount);

  // ── Coupon handlers ──────────────────────────────────────────────────────────
  const handleCouponApply = (code, discount, success) => {
    if (success) {
      setCouponCode(code);
      setCouponDiscount(discount);
      setCouponApplied(true);
      setCouponRedeemed(true);
    } else {
      setCouponApplied(false);
      setCouponDiscount(0);
      setCouponRedeemed(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(false);
    setCouponDiscount(0);
    setCouponCode("");
    setCouponRedeemed(false);
  };

  // ── Referral success callback (from ReferralModal) ───────────────────────────
  // Called with { referralCode, discountAmount, referrerId, referrerName }
  const handleReferralSuccess = ({ referralCode: code, discountAmount, referrerId: rid }) => {
    setReferralCode(code);
    setReferralDiscount(discountAmount);
    setReferrerId(rid);
    setReferralApplied(true);
  };

  const removeReferral = () => {
    setReferralApplied(false);
    setReferralDiscount(0);
    setReferralCode("");
    setReferrerId(null);
  };

  // ── Handle payment ───────────────────────────────────────────────────────────
  const handlePayment = async () => {
    if (!workoutId && !dietId) {
      alert("Please select a plan before proceeding.");
      return;
    }

    setIsProcessing(true);
    try {
      const result = await processPayment({
        workoutId,
        dietId,
        finalTotal,
        referralCode: referralApplied ? referralCode : null,
        referrerId:   referralApplied ? referrerId   : null,
        couponCode:   couponApplied   ? couponCode   : null,
      });

      if (result?.razorpay_order_id && couponApplied && !couponRedeemed) {
        try {
          await redeemCoupon(result.razorpay_order_id, couponCode);
          setCouponRedeemed(true);
        } catch (couponError) {
          console.error("Coupon redemption failed:", couponError);
        }
      }

      setOrderSuccess(true);
      navigate("/dashboard", { state: { paymentSuccess: true } });
    } catch (error) {
      console.error("❌ Payment error:", error);
      alert(error.message || "Payment failed.");
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Success screen ───────────────────────────────────────────────────────────
  if (orderSuccess) {
    return (
      <div className="bg-white font-sans min-h-screen flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="bg-lime-50 rounded-full w-24 h-24 mx-auto mb-6 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-lime-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Order Confirmed!</h2>
          <p className="text-gray-500 mb-8">
            Your subscription is now active. A confirmation email is on its way.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-lime-500 hover:bg-lime-600 text-black font-semibold px-10 py-3 rounded-full transition"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Guard: nothing selected ──────────────────────────────────────────────────
  if (!workoutId && !dietId) {
    return (
      <div className="bg-white font-sans min-h-screen">
        <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
          <div className="flex items-center gap-4 px-4 pt-6 pb-4 max-w-7xl mx-auto">
            <button onClick={() => navigate(-1)} className="text-gray-700">
              <ArrowLeft size={22} />
            </button>
            <h1 className="text-xl font-semibold text-gray-800">Checkout</h1>
          </div>
        </div>
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <p className="text-gray-500 mb-6">No plan selected. Please go back and choose a plan.</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-lime-500 hover:bg-lime-600 text-black font-semibold px-8 py-3 rounded-full transition"
          >
            ← Back to Plans
          </button>
        </div>
      </div>
    );
  }

  // ── Discount rows ────────────────────────────────────────────────────────────
  const DiscountRows = () =>
    totalDiscount > 0 ? (
      <div className="mb-4">
        <div className="border-t border-gray-200 pt-4 mb-3" />
        <h3 className="text-gray-900 font-bold text-sm mb-3">Discounts Applied</h3>

        {couponApplied && (
          <div className="flex items-center justify-between mb-2 text-green-600">
            <span className="text-sm">Coupon ({couponCode})</span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">-₹{couponDiscount}</span>
              <button onClick={removeCoupon} className="text-gray-400 hover:text-red-500">
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {referralApplied && (
          <div className="flex items-center justify-between mb-2 text-green-600">
            <span className="text-sm">Referral ({referralCode})</span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">-₹{referralDiscount}</span>
              <button onClick={removeReferral} className="text-gray-400 hover:text-red-500">
                <X size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    ) : null;

  // ── Discount buttons ─────────────────────────────────────────────────────────
  const DiscountButtons = () => (
    <>
      <button
        onClick={() => setShowCouponModal(true)}
        className="w-full flex items-center justify-between py-4 border-b border-gray-100 hover:bg-gray-50 transition"
        disabled={applyingCoupon}
      >
        <div className="flex items-center gap-4">
          {applyingCoupon ? (
            <Loader2 size={22} className="text-lime-500 animate-spin" />
          ) : (
            <Percent size={22} className="text-lime-500" />
          )}
          <span className="text-gray-800 font-semibold text-base">
            {couponApplied ? `✓ Coupon Applied (${couponCode})` : "Apply Coupon"}
          </span>
        </div>
        <ChevronRight size={18} className="text-gray-400" />
      </button>

      {/* <button
        onClick={() => setShowReferralModal(true)}
        className="w-full flex items-center justify-between py-4 border-b border-gray-100 hover:bg-gray-50 transition"
        disabled={referralApplied}
      >
        <div className="flex items-center gap-4">
          <Gift size={22} className="text-lime-500" />
          <span className="text-gray-800 font-semibold text-base">
            {referralApplied
              ? `✓ Referral Applied (${referralCode})`
              : "Apply Referral Code"}
          </span>
        </div>
        {!referralApplied && <ChevronRight size={18} className="text-gray-400" />}
      </button> */}
    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="bg-white font-sans min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="flex items-center gap-4 px-4 pt-6 pb-4 max-w-7xl mx-auto">
          <button onClick={() => navigate(-1)} className="text-gray-700">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-xl font-semibold text-gray-800">Checkout</h1>
        </div>
      </div>

      {/* Mobile */}
      {isMobile ? (
        <>
          <div className="px-4 pt-4 pb-36">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Order Summary</h2>

            {!isDietOnlyCheckout && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-800 font-semibold text-base">{selectedWorkout?.title}</span>
                  <span className="text-gray-900 font-semibold text-base">₹{workoutPrice}</span>
                </div>
                <div className="border-b border-gray-200 mb-4" />
              </>
            )}

            <h3 className="text-gray-900 font-bold text-base mb-4">Add-ons</h3>
            {selectedDiet ? (
              <div className="flex items-center justify-between mb-6">
                <span className="text-gray-600 text-base">{selectedDiet.title}</span>
                <span className="text-gray-800 font-bold text-base">₹{dietPrice}</span>
              </div>
            ) : (
              <p className="text-gray-400 text-sm mb-6">No add-ons selected</p>
            )}

            <DiscountRows />
            <DiscountButtons />
          </div>

          {/* Sticky pay bar */}
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-2xl border-t border-gray-100">
            <div className="p-4 space-y-2">
              {totalDiscount > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-green-600 text-sm">Total Discount</span>
                  <span className="text-green-600 font-semibold">-₹{Math.round(totalDiscount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-gray-900 text-lg font-bold">Total</span>
                <span className="text-gray-900 text-xl font-bold">₹{Math.round(finalTotal)}</span>
              </div>
              <button
                onClick={handlePayment}
                disabled={isProcessing}
                className="w-full mt-3 py-3 bg-lime-500 hover:bg-lime-600 font-bold text-black text-base rounded-full transition duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={18} className="animate-spin" />
                    Processing…
                  </span>
                ) : (
                  "Pay Now"
                )}
              </button>
            </div>
          </div>
        </>
      ) : (
        /* Desktop */
        <div className="max-w-7xl mx-auto px-6 pt-8 pb-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 rounded-2xl p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-6">Order Summary</h2>

                {!isDietOnlyCheckout && (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-gray-800 font-semibold text-base">{selectedWorkout?.title}</span>
                      <span className="text-gray-900 font-semibold text-base">₹{workoutPrice}</span>
                    </div>
                    <div className="border-b border-gray-200 mb-4" />
                  </>
                )}

                <h3 className="text-gray-900 font-bold text-base mb-4">Add-ons</h3>
                {selectedDiet ? (
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-gray-600 text-base">{selectedDiet.title}</span>
                    <span className="text-gray-800 font-bold text-base">₹{dietPrice}</span>
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm mb-6">No add-ons selected</p>
                )}

                <DiscountRows />
                <DiscountButtons />
              </div>
            </div>

            {/* Right */}
            <div>
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-24">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Summary</h3>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>₹{Math.round(subtotal)}</span>
                  </div>
                  {totalDiscount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Total Discount</span>
                      <span>-₹{Math.round(totalDiscount)}</span>
                    </div>
                  )}
                  <div className="border-t pt-3 flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-lime-600">₹{Math.round(finalTotal)}</span>
                  </div>
                </div>

                <button
                  onClick={handlePayment}
                  disabled={isProcessing}
                  className="w-full bg-lime-500 hover:bg-lime-600 text-black font-bold py-4 rounded-full transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Processing…
                    </>
                  ) : (
                    "Pay Now"
                  )}
                </button>

                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Shield size={16} /><span>Secure Payment Gateway</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Truck size={16} /><span>Instant Access After Payment</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock size={16} /><span>24/7 Customer Support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <CouponModal
        isOpen={showCouponModal}
        onClose={() => setShowCouponModal(false)}
        amount={subtotal}
        onCouponApplied={handleCouponApply}
        razorpayOrderId={razorpayOrderId}
      />

      <ReferralModal
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
        referralCode={referralCode}
        setReferralCode={setReferralCode}
        subtotal={subtotal}
        onSuccess={handleReferralSuccess}
      />
    </div>
  );
};

export default CheckoutPage;